import { useTranslation } from 'react-i18next';
import { DIAGRAM, diagramLayout } from './dataModel';
import type { IDataModelDiagramProps } from './IDataModelDiagramProps';

/** Кольори видів сутностей прототипу v4: основна — синя, дочірня — сланцева, довідник — фіолетовий пунктир. */
const KIND_COLOR = { main: '#1f4ebc', child: '#475569', catalogue: '#7c3aed' } as const;
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * Діаграма сутностей (прототип v4 mdDiagram): основна сутність угорі, решта рядами нижче з лініями кратності (1 → 0..*, довідник * → 1,
 * пунктиром), до восьми атрибутів у рамці (обов'язкові — «●» і жирним, тип праворуч), пігулки пов'язаних модулів унизу; клік по сутності
 * вибирає її, по пігулці — відкриває модель даних модуля.
 */
export function DataModelDiagram({ entities, selected, onSelect, related, moduleName }: IDataModelDiagramProps) {
  const { t } = useTranslation();
  const { W, BW, HH, RH, ROWS, PILL } = DIAGRAM;
  const layout = diagramLayout(entities.map((e) => e.attrs.length), related.length);
  const main = layout.boxes[0]!;
  return (
    <svg viewBox={`0 0 ${W} ${layout.height}`} width="100%" className="dm-diagram" role="img" aria-label={t('modDesign.model.diagramOf', { name: moduleName })}>
      {related.slice(0, layout.related.xs.length).map((r, i) => {
        const x = layout.related.xs[i]!;
        return (
          <g key={r.key} className="dm-pill" onClick={r.onOpen}>
            <title>{t('modDesign.model.openModel', { name: r.name })}</title>
            <line x1={main.x + BW / 2} y1={main.y + main.h / 2} x2={x + PILL / 2} y2={layout.related.y} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="2 4" />
            <rect x={x} y={layout.related.y} width={PILL} height={34} rx={17} fill="var(--surface2)" stroke="var(--accent)" strokeWidth={1.2} />
            <text x={x + PILL / 2} y={layout.related.y + 21} fontSize={11} fontWeight={600} fill="var(--ink)" textAnchor="middle">{r.label}</text>
          </g>
        );
      })}
      {entities.slice(1).map((e, i) => {
        const p = layout.boxes[i + 1]!;
        const [x1, y1, x2, y2] = [main.x + BW / 2, main.y + main.h, p.x + BW / 2, p.y];
        const my = (y1 + y2) / 2;
        const cat = e.kind === 'catalogue';
        return (
          <g key={`l${e.key}`}>
            <path d={`M${x1} ${y1} C${x1} ${my} ${x2} ${my} ${x2} ${y2}`} fill="none" stroke={cat ? '#a78bfa' : '#94a3b8'} strokeWidth={1.6} strokeDasharray={cat ? '5 3' : undefined} />
            <text x={x1 + (x2 > x1 ? 8 : -8)} y={y1 + 14} fontSize={10} fill="var(--muted)" textAnchor={x2 > x1 ? 'start' : 'end'}>{cat ? '*' : '1'}</text>
            <text x={x2 + (x2 > x1 ? -8 : 8)} y={y2 - 6} fontSize={10} fill="var(--muted)" textAnchor={x2 > x1 ? 'end' : 'start'}>{cat ? '1' : '0..*'}</text>
          </g>
        );
      })}
      {entities.map((e, i) => {
        const p = layout.boxes[i]!;
        const color = KIND_COLOR[e.kind];
        const { name } = e;
        return (
          <g key={`e${e.key}`} className="dm-box" onClick={() => onSelect(i)}>
            <title>{t('modDesign.model.boxTip', { name, count: e.attrs.length })}</title>
            <rect x={p.x} y={p.y} width={BW} height={p.h} rx={8} fill="var(--surface)" stroke={i === selected ? 'var(--accent)' : color}
              strokeWidth={i === selected ? 2.5 : 1.5} strokeDasharray={e.kind === 'catalogue' ? '5 3' : undefined} />
            <rect x={p.x} y={p.y} width={BW} height={HH} rx={8} fill={color} />
            <rect x={p.x} y={p.y + HH - 8} width={BW} height={8} fill={color} />
            <text x={p.x + 10} y={p.y + 17} fontSize={12} fontWeight={700} fill="#fff">{cut(name, 26)}</text>
            {e.attrs.slice(0, ROWS).map((f, j) => (
              <g key={`${f.key}${j}`}>
                <text x={p.x + 10} y={p.y + HH + 14 + j * RH} fontSize={11} fill="var(--ink)" fontWeight={f.required ? 700 : 400}>{`${f.required ? '● ' : ''}${cut(f.label, 17)}`}</text>
                <text x={p.x + BW - 10} y={p.y + HH + 14 + j * RH} fontSize={10} fill="var(--muted)" textAnchor="end" className="dm-mono">{`${f.typeName}${f.ref ? '→' : ''}`}</text>
              </g>
            ))}
            {e.attrs.length > ROWS && <text x={p.x + 10} y={p.y + HH + 14 + ROWS * RH} fontSize={10} fill="var(--muted)">{t('modDesign.model.more', { count: e.attrs.length - ROWS })}</text>}
          </g>
        );
      })}
    </svg>
  );
}
